import { resolveDcDefaultRate } from '../../Dashboard/TodoList/CostingTsx/calc/landed';
import type { ExchangeRateMasterEntry } from './exchange-rate.helpers';
import type { AutomatedFreightDto } from '../../../services/automated-freight.service';

/** The template alias of the Freight Automated default column. */
export const FREIGHT_AUTOMATED_ALIAS = 'freightAutomated';

/** Decimal places the per-unit freight is kept to before the landed table rounds it for display. */
const PER_UNIT_DECIMALS = 4;

/**
 * Whether a template's default columns carry an active Freight Automated column.
 * @param defaultColumns The template's default columns.
 * @returns True when Freight Automated is switched on.
 */
export function isFreightAutomatedActive(defaultColumns: ReadonlyArray<{ aliasName?: unknown; isActive?: boolean }> | undefined): boolean {
  return (defaultColumns ?? []).some((column) => column?.aliasName === FREIGHT_AUTOMATED_ALIAS && column?.isActive !== false);
}

/** What converting the freight into one distribution centre's currency depends on. */
export interface FreightConversionInput {
  exchangeRateList: ExchangeRateMasterEntry[] | undefined;
  dcCurrency: string | undefined;
  freightCurrency: string | null | undefined;
  supplierCurrency: string | null | undefined;
  dcExchangeRate: unknown;
  dcCompanyCurrencyValue: unknown;
}

/**
 * The rate that converts an amount in the freight row's currency into a distribution centre's
 * currency. Freight priced in the costing's supplier currency converts exactly as Price Per Unit
 * (Converted) and Total (Converted) do — `convertedCurrValue(value, exchangeRate, companyValue)`,
 * which divides by the distribution centre's exchange rate (manual override included) unless it
 * equals the centre's own base value — so editing that rate moves the freight too. Any other
 * currency is read from the season exchange-rate master. Same currency, or a freight row with no
 * currency, needs no conversion. A currency the master has no rate for is not guessed at: treating
 * it as 1 would show, say, a WST amount as rupees.
 * @param input The currencies, the season master and the distribution centre's exchange rate.
 * @returns The divisor to convert by, never zero, or null when there is no rate to convert with.
 */
export function freightConversionRate({ exchangeRateList, dcCurrency, freightCurrency, supplierCurrency, dcExchangeRate, dcCompanyCurrencyValue }: FreightConversionInput): number | null {
  if (!freightCurrency || !dcCurrency || freightCurrency === dcCurrency) return 1;
  if (freightCurrency === supplierCurrency) {
    const rate = Number(dcExchangeRate) || 1;
    const companyValue = Number(dcCompanyCurrencyValue) || 1;
    return rate > 0 && rate !== companyValue ? rate : 1;
  }
  return resolveFreightMasterRate(dcCurrency, freightCurrency, exchangeRateList);
}

/**
 * The automated freight per unit in a distribution centre's currency: the product's shipment cost
 * spread over the costing's total quantity, then converted — the "P.U." columns of the Costing /
 * Logistics sheet (`E20/B20`).
 * @param freight The product-level freight cost from the server.
 * @param totalQuantity The costing's total quantity across every row.
 * @param conversionRate The divisor from `freightConversionRate`.
 * @returns The per-unit amount, or null when the cost or the quantity is unavailable.
 */
export function automatedFreightPerUnit(freight: AutomatedFreightDto | null | undefined, totalQuantity: number, conversionRate: number | null): number | null {
  if (!freight || freight.status !== 'OK' || !freight.totalCost || !(totalQuantity > 0) || !conversionRate) return null;
  const perUnit = freight.totalCost / totalQuantity / conversionRate;
  const factor = 10 ** PER_UNIT_DECIMALS;
  return Math.round((perUnit + Number.EPSILON) * factor) / factor;
}

/**
 * The costing's total quantity: the sum of every landed row's quantity.
 * @param rows The landed rows.
 * @returns The total, 0 when no row carries a quantity.
 */
export function totalCostingQuantity(rows: ReadonlyArray<{ quantityPerSizCol?: unknown }>): number {
  return rows.reduce((sum, row) => {
    const quantity = Number(row?.quantityPerSizCol);
    return Number.isFinite(quantity) && quantity > 0 ? sum + quantity : sum;
  }, 0);
}

/**
 * The product's automated freight in a distribution centre's currency, before it is spread per
 * unit. Saved beside the per-unit amount so a quantity edit elsewhere (the costing to-do grid) can
 * re-spread it without the freight library.
 * @param freight The product-level freight cost from the server.
 * @param conversionRate The divisor from `freightConversionRate`.
 * @returns The converted total, or null when the cost is unavailable.
 */
export function automatedFreightTotal(freight: AutomatedFreightDto | null | undefined, conversionRate: number | null): number | null {
  if (!freight || freight.status !== 'OK' || !freight.totalCost || !conversionRate) return null;
  const total = freight.totalCost / conversionRate;
  const factor = 10 ** PER_UNIT_DECIMALS;
  return Math.round((total + Number.EPSILON) * factor) / factor;
}

/** Why a priced freight still cannot be shown for one distribution centre. */
export type FreightAutomatedRowIssue = '' | 'NO_EXCHANGE_RATE' | 'NO_QUANTITY';

/**
 * The reason a distribution centre's landed rows carry no automated freight even though the server
 * priced the product: no rate to convert the freight currency with, or no quantity to spread over.
 * @param freight The product-level freight cost from the server.
 * @param conversionRate The divisor from `freightConversionRate`.
 * @param totalQuantity The costing's total quantity.
 * @returns The issue, or an empty string when there is none.
 */
export function freightAutomatedRowIssue(freight: AutomatedFreightDto | null | undefined, conversionRate: number | null, totalQuantity: number): FreightAutomatedRowIssue {
  if (!freight || freight.status !== 'OK') return '';
  if (!conversionRate) return 'NO_EXCHANGE_RATE';
  if (!(totalQuantity > 0)) return 'NO_QUANTITY';
  return '';
}

/**
 * The season master rate that converts freight into a distribution centre's currency. The master is
 * read the way the supplier conversion reads it: the centre's currency entry, then the freight
 * currency under it (1 centre unit = rate freight units). A rate entered the other way round — the
 * freight currency's entry with the centre's currency under it — is accepted as its inverse, so a
 * rate typed under either currency converts the same amount. Mirrors `resolveFreightMasterRate` in
 * `bombiix-product-costing-create/src/services/landed-information.ts`.
 * @param dcCurrency The distribution centre's currency.
 * @param freightCurrency The freight row's currency.
 * @param exchangeRateList The season's exchange-rate master entries.
 * @returns The divisor to convert by, or null when neither direction has a positive rate.
 */
export function resolveFreightMasterRate(dcCurrency: string, freightCurrency: string, exchangeRateList: ExchangeRateMasterEntry[] | undefined): number | null {
  const direct = resolveDcDefaultRate({ companyCurrency: dcCurrency, supplierCurrency: freightCurrency }, exchangeRateList);
  if (direct && direct > 0) return direct;
  const reverse = resolveDcDefaultRate({ companyCurrency: freightCurrency, supplierCurrency: dcCurrency }, exchangeRateList);
  return reverse && reverse > 0 ? 1 / reverse : null;
}
